// Coordinate math for the infinite canvas. World coords are stored on items; screen coords
// are the actual pixels inside the canvas viewport. The "world" container has a CSS
// transform: translate(offset) scale(scale) applied to it, so:
//
//   screen = world * scale + offset
//   world  = (screen - offset) / scale

export type Point = { x: number; y: number }
export type Transform = { scale: number; offset: Point }

export function screenToWorld(p: Point, t: Transform): Point {
  return { x: (p.x - t.offset.x) / t.scale, y: (p.y - t.offset.y) / t.scale }
}

export function worldToScreen(p: Point, t: Transform): Point {
  return { x: p.x * t.scale + t.offset.x, y: p.y * t.scale + t.offset.y }
}

// Zoom centered on a screen point — adjust offset so the world point under the cursor
// stays fixed while the scale changes. Cursor-anchored zoom is what makes wheel-zoom feel
// natural; without this, the canvas drifts away from the user's focal point.
export function zoomAroundPoint(t: Transform, screenPoint: Point, nextScale: number): Transform {
  const clamped = Math.max(0.1, Math.min(4, nextScale))
  const world = screenToWorld(screenPoint, t)
  return {
    scale: clamped,
    offset: {
      x: screenPoint.x - world.x * clamped,
      y: screenPoint.y - world.y * clamped,
    },
  }
}

export type Rect = { x: number; y: number; w: number; h: number }

// AABB intersection — used by marquee selection to find items overlapping the user's box.
export function rectsIntersect(a: Rect, b: Rect): boolean {
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y)
}

// Find a placement for a new rect near a desired position that doesn't overlap
// any of the existing rects. Spiral search across 8 directions at increasing
// radii. Used at drop / paste / add time so new cards land in free space
// rather than on top of existing ones.
//
// `padding` inflates the candidate rect when checking overlap so neighbours
// get visible breathing room rather than getting butted up against each other.
//
// Returns the desired position when it's already free (within the padded
// envelope), otherwise the first non-overlapping candidate. Falls back to the
// desired position if no free spot is found within `maxRings` (the canvas is
// infinite — this cap just bounds the loop and keeps the worst case fast).
export function findFreeRect(
  desired: Rect,
  others: Rect[],
  options: { step?: number; maxRings?: number; padding?: number } = {},
): Rect {
  const { step = 32, maxRings = 30, padding = 20 } = options
  const inflated = (r: Rect): Rect => ({
    x: r.x - padding,
    y: r.y - padding,
    w: r.w + padding * 2,
    h: r.h + padding * 2,
  })
  const overlapsAny = (r: Rect): boolean => others.some((o) => rectsIntersect(inflated(r), o))
  if (!overlapsAny(desired)) return desired

  for (let ring = 1; ring <= maxRings; ring++) {
    const radius = ring * step
    // 8 compass directions per ring. Ordering biases toward "right then down"
    // for the first ring so cards stack in a familiar reading order when the
    // user drops several into the same hotspot.
    for (let angle = 0; angle < 8; angle++) {
      const rad = (angle * Math.PI) / 4
      const candidate: Rect = {
        x: desired.x + Math.cos(rad) * radius,
        y: desired.y + Math.sin(rad) * radius,
        w: desired.w,
        h: desired.h,
      }
      if (!overlapsAny(candidate)) return candidate
    }
  }
  return desired
}

// Compute a transform that fits all rects into the viewport with a percentage margin on
// each side. Returns null when there's nothing to fit or the viewport isn't sized yet.
// Caps scale at 1 so a single small item doesn't get zoomed past its native size.
export function computeFitTransform(
  rects: Rect[],
  viewport: { width: number; height: number },
  marginPct = 0.08,
): Transform | null {
  if (rects.length === 0) return null
  if (viewport.width <= 0 || viewport.height <= 0) return null

  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const r of rects) {
    if (r.x < x0) x0 = r.x
    if (r.y < y0) y0 = r.y
    if (r.x + r.w > x1) x1 = r.x + r.w
    if (r.y + r.h > y1) y1 = r.y + r.h
  }
  const contentW = Math.max(1, x1 - x0)
  const contentH = Math.max(1, y1 - y0)

  const availW = viewport.width * (1 - marginPct * 2)
  const availH = viewport.height * (1 - marginPct * 2)
  const scale = Math.min(availW / contentW, availH / contentH, 1)

  // Center the content inside the viewport at the computed scale.
  const screenW = contentW * scale
  const screenH = contentH * scale
  return {
    scale,
    offset: {
      x: (viewport.width - screenW) / 2 - x0 * scale,
      y: (viewport.height - screenH) / 2 - y0 * scale,
    },
  }
}
